import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getTraining, registerTraining, initializePayment } from '../utils/api';
import { toast } from 'sonner';

export const TrainingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', organization: '' });

  useEffect(() => {
    loadTraining();
  }, [id]);

  const loadTraining = async () => {
    try {
      const response = await getTraining(id);
      setTraining(response.data);
    } catch (error) {
      console.error('Error loading training:', error);
      toast.error('Training program not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const reg = await registerTraining({ training_id: training.id, ...formData, amount: training.fee });
      const payment = await initializePayment({ email: formData.email, amount: training.fee, payment_type: 'training', item_id: reg.data.id });
      window.location.href = payment.data.authorization_url;
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-xl">Loading...</p></div>;
  if (!training) return <div className="min-h-screen flex items-center justify-center"><p className="text-xl">Training program not found</p></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Button variant="ghost" onClick={() => navigate('/trainings')} className="text-white hover:bg-white/20 mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Trainings
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{fontFamily: 'Work Sans'}}>{training.title}</h1>
          <p className="text-xl text-green-100 mb-6">{training.category}</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <Clock className="h-5 w-5" />
              <span>{training.duration}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <Users className="h-5 w-5" />
              <span>Interactive Sessions</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Program Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-6">{training.description}</p>
                <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                  <p className="text-sm text-gray-700"><strong>Schedule:</strong> {training.schedule}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Course Syllabus</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {training.syllabus && training.syllabus.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="sticky top-24 space-y-6">
              <Card className="shadow-xl">
                <CardHeader className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-t-xl">
                  <CardTitle className="text-center">
                    <div className="text-4xl font-bold mb-2">₦{training.fee.toLocaleString()}</div>
                    <p className="text-sm text-green-100">Investment in Your Future</p>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input required className="mt-1" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" required className="mt-1" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input required className="mt-1" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                    </div>
                    <div>
                      <Label>Organization</Label>
                      <Input className="mt-1" value={formData.organization} onChange={(e) => setFormData({...formData, organization: e.target.value})} />
                    </div>
                    <Button type="submit" className="w-full h-12 text-lg font-semibold">Enroll Now</Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-4">What You'll Get</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" />Certificate of Completion</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" />Course Materials</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" />Expert Instruction</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" />Lifetime Support</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};